import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import { formatDuration } from "../../../helper/formatDuration";

const TrackMatchingPreview = ({ tracks, onTrackToggle }) => {
  const [expandedTrack, setExpandedTrack] = useState(null);
  const [filterType, setFilterType] = useState('all');

  const getMatchIcon = (confidence) => {
    if (confidence >= 0.9) return 'CheckCircle';
    if (confidence >= 0.7) return 'AlertCircle';
    return 'XCircle';
  };

  const getMatchColor = (confidence) => {
    if (confidence >= 0.9) return 'text-success';
    if (confidence >= 0.7) return 'text-warning';
    return 'text-error';
  };

  const getMatchBadge = (confidence) => {
    if (confidence >= 0.9) return { text: 'Exact Match', class: 'bg-success/10 text-success' };
    if (confidence >= 0.7) return { text: 'Similar Match', class: 'bg-warning/10 text-warning' };
    return { text: 'No Match', class: 'bg-error/10 text-error' };
  };

  

  const filteredTracks = tracks.filter(track => {
    if (filterType === 'all') return true;
    if (filterType === 'exact') return track.matchConfidence >= 0.9;
    if (filterType === 'similar') return track.matchConfidence >= 0.7 && track.matchConfidence < 0.9;
    if (filterType === 'no-match') return track.matchConfidence < 0.7;
    return true;
  });

  const matchStats = {
    exact: tracks.filter(t => t.matchConfidence >= 0.9).length,
    similar: tracks.filter(t => t.matchConfidence >= 0.7 && t.matchConfidence < 0.9).length,
    noMatch: tracks.filter(t => t.matchConfidence < 0.7).length
  };

  return (
    <div className="bg-surface border border-border rounded-lg">
      <div className="p-4 lg:p-6 border-b border-border">
        <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between space-y-4 lg:space-y-0">
          <div>
            <h3 className="text-lg font-semibold text-text-primary mb-2">
              Track Matching Preview
            </h3>
            <div className="flex flex-wrap items-center gap-4 text-sm">
              <div className="flex items-center space-x-1 text-success">
                <Icon name="CheckCircle" size={16} />
                <span>{matchStats.exact} exact matches</span>
              </div>
              <div className="flex items-center space-x-1 text-warning">
                <Icon name="AlertCircle" size={16} />
                <span>{matchStats.similar} similar matches</span>
              </div>
              <div className="flex items-center space-x-1 text-error">
                <Icon name="XCircle" size={16} />
                <span>{matchStats.noMatch} no matches</span>
              </div>
            </div>
          </div>

          <div className="flex items-center space-x-2">
            <select
              value={filterType}
              onChange={(e) => setFilterType(e.target.value)}
              className="px-3 py-2 bg-background border border-border rounded-lg text-sm text-text-primary focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
            >
              <option value="all">All Tracks ({tracks.length})</option>
              <option value="exact">Exact Matches ({matchStats.exact})</option>
              <option value="similar">Similar Matches ({matchStats.similar})</option>
              <option value="no-match">No Matches ({matchStats.noMatch})</option>
            </select>
          </div>
        </div>
      </div>

      <div className="max-h-96 overflow-y-auto">
        {filteredTracks.map((track, index) => {
          const isExpanded = expandedTrack === track.id;
          const matchBadge = getMatchBadge(track.matchConfidence);

          return (
            <div key={track.id} className="border-b border-border last:border-b-0">
              <div className="p-4 hover:bg-surface-secondary transition-smooth">
                <div className="flex items-center space-x-3">
                  <div className="flex-shrink-0">
                    <Icon 
                      name={getMatchIcon(track.matchConfidence)} 
                      size={20} 
                      className={getMatchColor(track.matchConfidence)}
                    />
                  </div>

                  <div className="flex-1 min-w-0">
                    <div className="flex items-start justify-between">
                      <div className="flex-1 min-w-0">
                        <h4 className="text-sm font-medium text-text-primary truncate">
                          {track.title}
                        </h4>
                        <p className="text-sm text-text-secondary truncate">
                          {track.artist} • {track.album}
                        </p>
                        <div className="flex items-center space-x-2 mt-1">
                          <span className="text-xs text-text-muted">
                            {formatDuration(track.duration)}
                          </span>
                          <div className={`px-2 py-0.5 rounded-full text-xs font-medium ${matchBadge.class}`}>
                            {matchBadge.text}
                          </div>
                        </div>
                      </div>

                      <div className="flex items-center space-x-2 ml-4">
                        {track.matchConfidence < 0.9 && (
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => setExpandedTrack(isExpanded ? null : track.id)}
                            iconName={isExpanded ? "ChevronUp" : "ChevronDown"}
                            iconSize={16}
                          >
                            {isExpanded ? 'Less' : 'Details'}
                          </Button>
                        )}
                        
                        <label className="flex items-center space-x-2 cursor-pointer">
                          <input
                            type="checkbox"
                            checked={track.selected}
                            onChange={() => onTrackToggle(track.id)}
                            className="w-4 h-4 text-primary bg-background border-border rounded focus:ring-primary focus:ring-2"
                          />
                          <span className="text-xs text-text-muted">Include</span>
                        </label>
                      </div>
                    </div>
                  </div>
                </div>

                {isExpanded && track.potentialMatches && (
                  <div className="mt-4 pl-8 border-l-2 border-border">
                    <h5 className="text-sm font-medium text-text-primary mb-2">
                      Potential Matches Found:
                    </h5>
                    <div className="space-y-2">
                      {track.potentialMatches.map((match, matchIndex) => (
                        <div key={matchIndex} className="flex items-center justify-between p-2 bg-background rounded-lg">
                          <div className="flex-1">
                            <p className="text-sm text-text-primary">
                              {match.title} • {match.artist}
                            </p>
                            <p className="text-xs text-text-muted">
                              {formatDuration(match.duration)} • {Math.round(match.confidence * 100)}% match
                            </p>
                          </div>
                          <Button
                            variant="ghost"
                            size="sm"
                            className="text-primary hover:text-primary hover:bg-primary/10"
                          >
                            Select This
                          </Button>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </div>
          );
        })}
      </div>

      {filteredTracks.length === 0 && (
        <div className="p-8 text-center">
          <Icon name="Search" size={48} className="text-text-muted mx-auto mb-4" />
          <p className="text-text-muted">No tracks match the current filter.</p>
        </div>
      )}
    </div>
  );
};

export default TrackMatchingPreview;